import { useState, useEffect } from "react";
import { ChevronDown, BookOpen, Download, Settings, Monitor, Palette, BarChart3 } from "lucide-react";
import { SEO } from "@/components/seo";

const tutorialData = [
  {
    category: "Getting Started",
    icon: Download,
    tutorials: [
      {
        title: "How to Install Sierra Chart Templates",
        content: `Installing a Sierra Chart template from Sierra Chart Pro is straightforward. Follow these steps to get your new template up and running:

1. Purchase and download your template file from the Purchases page in your account.
2. Open Sierra Chart on your computer.
3. Navigate to File > Open Chartbook from the top menu bar.
4. Browse to the location where you saved the downloaded template file and select it.
5. The chartbook will open with all pre-configured layouts, studies, and settings already applied.

Your existing chartbooks and settings will not be affected. Templates open as separate chartbooks, so you can safely try them out without disturbing your current setup.

If the template doesn't load correctly, ensure you're running the latest version of Sierra Chart and try re-downloading the file from your Purchases page.`,
      },
      {
        title: "How to Install Custom Indicators (.dll Studies)",
        content: `Custom indicators require a slightly different installation process since they use compiled study files. Here's how to install them:

1. Download the indicator .dll file from your Purchases page.
2. Locate your Sierra Chart Data folder. By default, this is typically at C:\\SierraChart\\Data on Windows.
3. Copy the downloaded .dll file into the Data folder (or the appropriate ACS_Source subfolder if specified in the product instructions).
4. Restart Sierra Chart completely — close and reopen the application.
5. Open your chart, then go to Analysis > Studies to add the new indicator.
6. Search for the indicator name in the studies list. It will appear under "Custom Studies" or by its specific name.
7. Select it, click Add, and configure any settings as needed.

Each indicator product includes specific configuration instructions. Refer to the documentation provided with your purchase for optimal settings and recommended chart timeframes.`,
      },
    ],
  },
  {
    category: "Chart Configuration",
    icon: Settings,
    tutorials: [
      {
        title: "Setting Up Market Profile (TPO) Charts",
        content: `Market Profile TPO (Time Price Opportunity) charts are a powerful way to visualize where price spends the most time. Here's how to configure them in Sierra Chart:

1. Open your Market Profile template or create a new chartbook.
2. Go to Analysis > Studies and add the "TPO Profile Chart" study.
3. In the study settings, configure the following key parameters:
   - TPO Letter Increment: Set the time interval each letter represents (typically 30 minutes for day session profiles).
   - Tick Size for Letters: Determines the price increment for each TPO block.
   - Display Volume: Enable to show volume alongside the TPO profile.
4. Adjust the session times under Chart > Chart Settings > Session Times to match your market's trading hours.
5. Set the Initial Balance period (usually the first hour of trading) to highlight the opening range.

Key concepts to watch for:
- Value Area: The range where approximately 70% of trading activity occurs.
- Point of Control (POC): The price level with the most TPO prints, indicating the fairest price.
- Single Prints: Areas of rapid price movement that may act as future support or resistance.

Our Market Profile templates come pre-configured with optimal settings for popular futures markets including ES, NQ, CL, and GC.`,
      },
      {
        title: "Configuring Footprint / Orderflow Charts",
        content: `Footprint charts provide granular insight into the buying and selling activity at each price level. Follow these steps to set up footprint charts in Sierra Chart:

1. Open a new or existing chart with tick-level data enabled (ensure your data feed provides bid/ask data).
2. Go to Analysis > Studies and add the "Numbers Bars" study. This is Sierra Chart's built-in footprint chart functionality.
3. Configure the Numbers Bars study settings:
   - Numbers Bars Type: Choose from Bid/Ask Volume, Delta, or Total Volume depending on your analysis preference.
   - Text Display Format: Select how numbers are displayed (e.g., "BidVol x AskVol").
   - Coloring: Set colors for positive delta (buyers) and negative delta (sellers).
4. Under Chart > Chart Settings, set your bar period to a suitable timeframe (5-minute or 15-minute bars work well for intraday analysis).
5. Enable the Volume Profile overlay to see the distribution of volume across price levels within each bar.

Reading Footprint Charts:
- Imbalances: Look for areas where buying or selling pressure significantly outweighs the other side. These stacked imbalances can signal strong directional intent.
- Absorption: When heavy volume occurs at a level but price doesn't move through it, this may indicate a strong buyer or seller absorbing orders.
- Exhaustion: Decreasing delta at price extremes may signal a potential reversal.

Our Footprint templates include color-coded delta visualization, automatic imbalance detection, and volume-weighted price analysis.`,
      },
    ],
  },
  {
    category: "Workspace & Layout",
    icon: Monitor,
    tutorials: [
      {
        title: "Organizing Multi-Monitor Workspaces",
        content: `Professional traders often use multiple monitors. Sierra Chart supports multi-monitor setups natively. Here's how to optimize your workspace:

1. Open Sierra Chart and create or open your chartbooks.
2. To move a chart window to another monitor, simply drag it from the main Sierra Chart window to the desired screen.
3. Use Window > Save Window Layout to save your multi-monitor arrangement.
4. Create different chartbook configurations for different market sessions or trading strategies.

Best Practices for Multi-Monitor Setups:
- Monitor 1 (Primary): Keep your active trading chart with orderflow/footprint data front and center.
- Monitor 2: Display Market Profile and Volume Profile charts for context and key levels.
- Monitor 3: Use for the DOM (Depth of Market), trade management, and position monitoring.
- Monitor 4 (if available): Keep higher timeframe charts, correlated markets, or news feeds.

In Sierra Chart, go to Global Settings > General Settings and enable "Allow Charts to be Moved Outside of Main Window" if charts aren't detaching properly.

You can also use Sierra Chart's built-in chartbook tabs to quickly switch between different analysis setups on a single monitor. Assign keyboard shortcuts via Global Settings > Keyboard Shortcuts for rapid switching.`,
      },
    ],
  },
  {
    category: "Customization & Styling",
    icon: Palette,
    tutorials: [
      {
        title: "Customizing Chart Colors and Appearance",
        content: `Personalizing your chart colors can improve readability and reduce eye strain during long trading sessions. Here's how to customize the appearance of your charts:

1. Right-click on your chart and select Chart Settings, or go to Chart > Chart Settings.
2. Navigate to the "Chart Colors" section to modify background, grid lines, text, and candle colors.
3. For candlestick charts:
   - Set the Up Candle Color (bullish) and Down Candle Color (bearish) to high-contrast colors.
   - Adjust the candle border colors for better visibility.
4. Background Color: Dark backgrounds (dark gray or black) are popular among professional traders for reduced eye strain.
5. Grid Lines: Set grid lines to a subtle color that doesn't distract from price action.

To apply your color scheme across all charts:
- Configure one chart with your desired colors.
- Go to Chart > Save Chart Drawing Appearance as Default.
- New charts will inherit these settings automatically.

For study-specific colors (e.g., moving averages, volume bars), go to Analysis > Studies, select the study, and modify its color settings individually.

Our templates come with professionally designed color schemes optimized for clarity in both bright and dim lighting conditions. You can further adjust these to match your personal preferences.`,
      },
      {
        title: "Creating and Saving Custom Chart Templates",
        content: `Once you've configured your charts exactly how you want them, you can save them as reusable templates:

1. Set up your chart with all desired studies, indicators, settings, and colors.
2. Go to File > Save Chartbook As and save it with a descriptive name (e.g., "ES_5min_Footprint_v2").
3. Store your custom chartbooks in an organized folder structure, such as:
   - /Chartbooks/Templates/ for base templates
   - /Chartbooks/Active/ for current trading setups
   - /Chartbooks/Archive/ for older configurations

To apply a saved template to a new symbol:
1. Open the saved chartbook.
2. Go to Chart > Chart Settings and change the Symbol field to your new instrument.
3. Save with a new name to preserve the original template.

Backup Strategy:
- Regularly back up your Sierra Chart Data folder, which contains all your chartbooks, study settings, and custom configurations.
- Consider using a cloud sync service on your chartbook folder for automatic backups.
- Before updating Sierra Chart or installing new studies, always create a backup of your current Data folder.`,
      },
    ],
  },
  {
    category: "Advanced Techniques",
    icon: BarChart3,
    tutorials: [
      {
        title: "Using Volume Profile for Support and Resistance",
        content: `Volume Profile is one of the most powerful tools for identifying significant price levels. Here's how to use it effectively:

Understanding Volume Profile Components:
- Point of Control (POC): The price level with the highest traded volume. Acts as a magnet for price and represents fair value.
- Value Area High (VAH): The upper boundary of the value area (typically 70% of volume). Often acts as resistance.
- Value Area Low (VAL): The lower boundary of the value area. Often acts as support.
- High Volume Nodes (HVN): Price levels with significant volume concentration — these tend to attract price and act as support/resistance.
- Low Volume Nodes (LVN): Price levels with minimal trading volume — price tends to move quickly through these areas.

Setting Up Volume Profile in Sierra Chart:
1. Add the "Volume by Price" study via Analysis > Studies.
2. Configure the profile period (Session, Weekly, Monthly, or Visible Range).
3. Enable POC, VAH, and VAL lines for quick reference.
4. Adjust the number of price levels (more levels = finer granularity).

Trading with Volume Profile:
- When price approaches a prior session's POC, expect potential support or resistance.
- Breakouts from the Value Area often lead to trending moves, especially with strong delta.
- Composite profiles (multi-day) reveal longer-term support and resistance zones.
- Watch for "naked" POCs — previous session POCs that price hasn't revisited — as these tend to be revisited eventually.

Our Volume Profile templates include multi-timeframe profiles, composite views, and automatic level detection for streamlined analysis.`,
      },
      {
        title: "Delta Analysis and Cumulative Delta Strategies",
        content: `Delta analysis measures the difference between buying and selling pressure at each price level, providing insight into market sentiment and potential reversals.

Key Delta Concepts:
- Bar Delta: The net difference between market buy orders and market sell orders within a single bar.
- Cumulative Delta: The running total of delta over time, showing the overall bias of buyers vs. sellers.
- Delta Divergence: When price makes a new high but cumulative delta does not (or vice versa), suggesting weakening momentum.

Setting Up Delta Studies:
1. In Sierra Chart, go to Analysis > Studies and add "Cumulative Delta Bars - Volume."
2. Configure the study to display as a separate chart pane below your main price chart.
3. Add a moving average to the cumulative delta for trend identification.
4. Optionally, add the "Delta Volume - Per Bar" study for bar-by-bar delta analysis.

Trading Strategies Using Delta:
- Divergence Trading: If price makes higher highs but cumulative delta makes lower highs, selling pressure is increasing — watch for a potential reversal.
- Confirmation: Strong cumulative delta in the direction of a breakout confirms genuine buying/selling interest behind the move.
- Absorption Detection: If delta is heavily negative but price isn't dropping, it suggests a large buyer is absorbing sell orders — a potential bullish signal.
- Speed of Delta: Rapid changes in cumulative delta slope can indicate the beginning of aggressive buying or selling.

Our Orderflow Suite indicators include pre-configured delta analysis with automatic divergence detection, imbalance alerts, and multi-timeframe delta comparison tools.`,
      },
    ],
  },
];

function TutorialItem({ title, content }: { title: string; content: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0" data-testid={`tutorial-item-${title.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 py-4 px-1 text-left hover:text-muted-foreground transition-colors"
        aria-expanded={isOpen}
        data-testid={`button-tutorial-toggle-${title.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      >
        <span className="font-medium text-sm sm:text-base pr-4">{title}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed px-1 whitespace-pre-line">{content}</p>
        </div>
      )}
    </div>
  );
}

export default function TutorialsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="Knowledge Base & Tutorials"
        description="Learn how to install, configure, and customize Sierra Chart templates and indicators. Step-by-step guides for Market Profile, Footprint Charts, Volume Profile, and more."
        keywords="Sierra Chart tutorials, Sierra Chart setup guide, how to install Sierra Chart templates, Market Profile tutorial, Footprint chart guide, Volume Profile setup"
        canonical="/tutorials"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Sierra Chart Setup and Configuration Tutorials",
          description: "Step-by-step guides for installing, configuring, and customizing Sierra Chart templates and indicators.",
        }}
      />

      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="h-7 w-7 text-muted-foreground" />
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="text-tutorials-title">Knowledge Base & Tutorials</h1>
      </div>
      <p className="text-muted-foreground mb-8" data-testid="text-tutorials-description">
        Step-by-step guides to help you install, configure, and get the most out of your Sierra Chart templates and indicators.
      </p>

      <div className="space-y-8">
        {tutorialData.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.category}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold" data-testid={`text-tutorial-category-${section.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {section.category}
                </h2>
              </div>
              <div className="rounded-lg border border-border bg-card">
                {section.tutorials.map((item) => (
                  <TutorialItem key={item.title} title={item.title} content={item.content} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
